import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
    BankStatement,
    BankStatementColumnMapping,
    BankStatementImportCommitRequest,
    BankStatementImportCommitResponse,
    BankStatementImportPreview,
    BankStatementLinesResponse,
    BankStatementLineStatus,
    BankStatementStatus,
    BankReconciliationAuditEvent,
    BankReconciliationAdjustment,
    BankReconciliationAdjustmentRequest,
    BankReconciliationAutoMatchResponse,
    BankReconciliationCandidateSearchResponse,
    BankReconciliationCreateSessionRequest,
    BankReconciliationDashboard,
    BankReconciliationException,
    BankReconciliationExceptionAgingRow,
    BankReconciliationExceptionRequest,
    BankReconciliationExceptionStatus,
    BankReconciliationMatchDetail,
    BankReconciliationMatchRequest,
    BankReconciliationOutstandingItemRow,
    BankReconciliationPagedReportResponse,
    BankReconciliationReopenRequest,
    BankReconciliationReportFilter,
    BankReconciliationRule,
    BankReconciliationSession,
    BankReconciliationSourceType,
    BankReconciliationStatementReport,
    BankReconciliationTransitionRequest,
    BankReconciliationUnclearedBookRow,
    BankReconciliationUnmatchedBankLineRow,
    BankReconciliationVarianceRow,
    BankReconciliationWorkspace
} from './models/bank-reconciliation.model';

@Injectable({
    providedIn: 'root'
})
export class BankReconciliationService {
    private readonly baseUrl = `${environment.restApiUrl}bank-reconciliation`;

    constructor(private http: HttpClient) {}

    getDashboard(companyId: number, bankAccountId?: number): Observable<BankReconciliationDashboard> {
        let params = new HttpParams().set('companyId', String(companyId));
        if (bankAccountId !== undefined) params = params.set('bankAccountId', String(bankAccountId));
        return this.http.get<BankReconciliationDashboard>(`${this.baseUrl}/dashboard`, { params });
    }

    getStatements(companyId: number, bankAccountId?: number, status?: BankStatementStatus): Observable<BankStatement[]> {
        let params = new HttpParams().set('companyId', companyId.toString());
        if (bankAccountId) params = params.set('bankAccountId', bankAccountId.toString());
        if (status) params = params.set('status', status);
        return this.http.get<BankStatement[]>(`${this.baseUrl}/statements`, { params });
    }

    previewImport(companyId: number, bankAccountId: number, file: File, mapping?: BankStatementColumnMapping): Observable<BankStatementImportPreview> {
        const formData = new FormData();
        formData.append('file', file);
        let params = new HttpParams()
            .set('companyId', companyId.toString())
            .set('bankAccountId', bankAccountId.toString());
        if (mapping && Object.values(mapping).some(value => !!value)) {
            params = params.set('columnMappingJson', JSON.stringify(mapping));
        }
        return this.http.post<BankStatementImportPreview>(`${this.baseUrl}/statements/import/preview`, formData, { params });
    }

    getImportPreview(importId: number): Observable<BankStatementImportPreview> {
        return this.http.get<BankStatementImportPreview>(`${this.baseUrl}/statements/imports/${importId}`);
    }

    commitImport(importId: number, request: BankStatementImportCommitRequest): Observable<BankStatementImportCommitResponse> {
        return this.http.post<BankStatementImportCommitResponse>(`${this.baseUrl}/statements/import/${importId}/commit`, request);
    }

    getStatement(statementId: number): Observable<BankStatement> {
        return this.http.get<BankStatement>(`${this.baseUrl}/statements/${statementId}`);
    }

    getStatementSessions(statementId: number): Observable<BankReconciliationSession[]> {
        return this.http.get<BankReconciliationSession[]>(`${this.baseUrl}/statements/${statementId}/sessions`);
    }

    getStatementLines(
        statementId: number,
        options: { status?: BankStatementLineStatus | ''; searchText?: string; startDate?: string; endDate?: string; page?: number; size?: number } = {}
    ): Observable<BankStatementLinesResponse> {
        let params = new HttpParams()
            .set('page', String(options.page ?? 0))
            .set('size', String(options.size ?? 50));
        if (options.status) params = params.set('status', options.status);
        if (options.searchText) params = params.set('searchText', options.searchText);
        if (options.startDate) params = params.set('startDate', options.startDate);
        if (options.endDate) params = params.set('endDate', options.endDate);
        return this.http.get<BankStatementLinesResponse>(`${this.baseUrl}/statements/${statementId}/lines`, { params });
    }

    createSession(request: BankReconciliationCreateSessionRequest): Observable<BankReconciliationSession> {
        return this.http.post<BankReconciliationSession>(`${this.baseUrl}/sessions`, request);
    }

    submitSession(sessionId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationSession> {
        return this.http.post<BankReconciliationSession>(`${this.baseUrl}/sessions/${sessionId}/submit`, request);
    }

    approveSession(sessionId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationSession> {
        return this.http.post<BankReconciliationSession>(`${this.baseUrl}/sessions/${sessionId}/approve`, request);
    }

    closeSession(sessionId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationSession> {
        return this.http.post<BankReconciliationSession>(`${this.baseUrl}/sessions/${sessionId}/close`, request);
    }

    reopenSession(sessionId: number, request: BankReconciliationReopenRequest): Observable<BankReconciliationSession> {
        return this.http.post<BankReconciliationSession>(`${this.baseUrl}/sessions/${sessionId}/reopen`, request);
    }

    cancelSession(sessionId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationSession> {
        return this.http.post<BankReconciliationSession>(`${this.baseUrl}/sessions/${sessionId}/cancel`, request);
    }

    getWorkspace(sessionId: number): Observable<BankReconciliationWorkspace> {
        return this.http.get<BankReconciliationWorkspace>(`${this.baseUrl}/sessions/${sessionId}/workspace`);
    }

    getSessionReport(sessionId: number): Observable<BankReconciliationStatementReport> {
        return this.http.get<BankReconciliationStatementReport>(`${this.baseUrl}/sessions/${sessionId}/report`);
    }

    searchSessionCandidates(
        sessionId: number,
        options: {
            searchText?: string;
            sourceType?: BankReconciliationSourceType | '';
            entryDateFrom?: string;
            entryDateTo?: string;
            minAmount?: number;
            maxAmount?: number;
            currencyCode?: string;
            partnerId?: number;
            partnerType?: string;
            page?: number;
            size?: number;
        } = {}
    ): Observable<BankReconciliationCandidateSearchResponse> {
        let params = new HttpParams()
            .set('page', String(options.page ?? 0))
            .set('size', String(options.size ?? 50));
        if (options.searchText) params = params.set('searchText', options.searchText);
        if (options.sourceType) params = params.set('sourceType', options.sourceType);
        if (options.entryDateFrom) params = params.set('entryDateFrom', options.entryDateFrom);
        if (options.entryDateTo) params = params.set('entryDateTo', options.entryDateTo);
        if (options.minAmount !== undefined) params = params.set('minAmount', String(options.minAmount));
        if (options.maxAmount !== undefined) params = params.set('maxAmount', String(options.maxAmount));
        if (options.currencyCode) params = params.set('currencyCode', options.currencyCode);
        if (options.partnerId !== undefined) params = params.set('partnerId', String(options.partnerId));
        if (options.partnerType) params = params.set('partnerType', options.partnerType);
        return this.http.get<BankReconciliationCandidateSearchResponse>(`${this.baseUrl}/sessions/${sessionId}/candidates`, { params });
    }

    createMatch(sessionId: number, request: BankReconciliationMatchRequest): Observable<unknown> {
        return this.http.post(`${this.baseUrl}/sessions/${sessionId}/matches`, request);
    }

    getMatches(sessionId: number): Observable<BankReconciliationMatchDetail[]> {
        return this.http.get<BankReconciliationMatchDetail[]>(`${this.baseUrl}/sessions/${sessionId}/matches`);
    }

    deleteMatch(sessionId: number, matchId: number, performedBy?: number): Observable<void> {
        let params = new HttpParams();
        if (performedBy !== undefined) params = params.set('performedBy', String(performedBy));
        return this.http.delete<void>(`${this.baseUrl}/sessions/${sessionId}/matches/${matchId}`, { params });
    }

    getAuditEvents(sessionId: number): Observable<BankReconciliationAuditEvent[]> {
        return this.http.get<BankReconciliationAuditEvent[]>(`${this.baseUrl}/sessions/${sessionId}/audit-events`);
    }

    getAdjustments(sessionId: number): Observable<BankReconciliationAdjustment[]> {
        return this.http.get<BankReconciliationAdjustment[]>(`${this.baseUrl}/sessions/${sessionId}/adjustments`);
    }

    createAdjustment(sessionId: number, request: BankReconciliationAdjustmentRequest): Observable<BankReconciliationAdjustment> {
        return this.http.post<BankReconciliationAdjustment>(`${this.baseUrl}/sessions/${sessionId}/adjustments`, request);
    }

    approveAdjustment(sessionId: number, adjustmentId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationAdjustment> {
        return this.http.post<BankReconciliationAdjustment>(`${this.baseUrl}/sessions/${sessionId}/adjustments/${adjustmentId}/approve`, request);
    }

    cancelAdjustment(sessionId: number, adjustmentId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationAdjustment> {
        return this.http.post<BankReconciliationAdjustment>(`${this.baseUrl}/sessions/${sessionId}/adjustments/${adjustmentId}/cancel`, request);
    }

    getSessionExceptions(sessionId: number): Observable<BankReconciliationException[]> {
        return this.http.get<BankReconciliationException[]>(`${this.baseUrl}/sessions/${sessionId}/exceptions`);
    }

    createException(sessionId: number, request: BankReconciliationExceptionRequest): Observable<BankReconciliationException> {
        return this.http.post<BankReconciliationException>(`${this.baseUrl}/sessions/${sessionId}/exceptions`, request);
    }

    getExceptions(companyId: number, options: { status?: BankReconciliationExceptionStatus | ''; bankAccountId?: number; sessionId?: number } = {}): Observable<BankReconciliationException[]> {
        let params = new HttpParams().set('companyId', String(companyId));
        if (options.status) params = params.set('status', options.status);
        if (options.bankAccountId !== undefined) params = params.set('bankAccountId', String(options.bankAccountId));
        if (options.sessionId !== undefined) params = params.set('sessionId', String(options.sessionId));
        return this.http.get<BankReconciliationException[]>(`${this.baseUrl}/exceptions`, { params });
    }

    reviewException(exceptionId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationException> {
        return this.http.post<BankReconciliationException>(`${this.baseUrl}/exceptions/${exceptionId}/review`, request);
    }

    resolveException(exceptionId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationException> {
        return this.http.post<BankReconciliationException>(`${this.baseUrl}/exceptions/${exceptionId}/resolve`, request);
    }

    excludeException(exceptionId: number, request: BankReconciliationTransitionRequest): Observable<BankReconciliationException> {
        return this.http.post<BankReconciliationException>(`${this.baseUrl}/exceptions/${exceptionId}/exclude`, request);
    }

    autoMatch(sessionId: number, apply: boolean, performedBy?: number): Observable<BankReconciliationAutoMatchResponse> {
        let params = new HttpParams().set('apply', String(apply));
        if (performedBy !== undefined) params = params.set('performedBy', String(performedBy));
        return this.http.post<BankReconciliationAutoMatchResponse>(`${this.baseUrl}/sessions/${sessionId}/auto-match`, null, { params });
    }

    getRules(companyId: number, bankAccountId?: number): Observable<BankReconciliationRule[]> {
        let params = new HttpParams().set('companyId', String(companyId));
        if (bankAccountId) params = params.set('bankAccountId', String(bankAccountId));
        return this.http.get<BankReconciliationRule[]>(`${this.baseUrl}/rules`, { params });
    }

    createRule(rule: BankReconciliationRule): Observable<BankReconciliationRule> {
        return this.http.post<BankReconciliationRule>(`${this.baseUrl}/rules`, rule);
    }

    updateRule(ruleId: number, rule: BankReconciliationRule): Observable<BankReconciliationRule> {
        return this.http.put<BankReconciliationRule>(`${this.baseUrl}/rules/${ruleId}`, rule);
    }

    deleteRule(ruleId: number): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/rules/${ruleId}`);
    }

    getUnmatchedBankLinesReport(companyId: number, filter: BankReconciliationReportFilter = {}): Observable<BankReconciliationPagedReportResponse<BankReconciliationUnmatchedBankLineRow>> {
        return this.http.get<BankReconciliationPagedReportResponse<BankReconciliationUnmatchedBankLineRow>>(`${this.baseUrl}/reports/unmatched-bank-lines`, { params: this.reportParams(companyId, filter) });
    }

    getUnclearedBookTransactionsReport(companyId: number, filter: BankReconciliationReportFilter = {}): Observable<BankReconciliationPagedReportResponse<BankReconciliationUnclearedBookRow>> {
        return this.http.get<BankReconciliationPagedReportResponse<BankReconciliationUnclearedBookRow>>(`${this.baseUrl}/reports/uncleared-book-transactions`, { params: this.reportParams(companyId, filter) });
    }

    getOutstandingItemsReport(companyId: number, filter: BankReconciliationReportFilter = {}): Observable<BankReconciliationPagedReportResponse<BankReconciliationOutstandingItemRow>> {
        return this.http.get<BankReconciliationPagedReportResponse<BankReconciliationOutstandingItemRow>>(`${this.baseUrl}/reports/outstanding-items`, { params: this.reportParams(companyId, filter) });
    }

    getVarianceReport(companyId: number, filter: BankReconciliationReportFilter = {}): Observable<BankReconciliationPagedReportResponse<BankReconciliationVarianceRow>> {
        return this.http.get<BankReconciliationPagedReportResponse<BankReconciliationVarianceRow>>(`${this.baseUrl}/reports/variance`, { params: this.reportParams(companyId, filter) });
    }

    getExceptionAgingReport(companyId: number, filter: BankReconciliationReportFilter = {}): Observable<BankReconciliationPagedReportResponse<BankReconciliationExceptionAgingRow>> {
        return this.http.get<BankReconciliationPagedReportResponse<BankReconciliationExceptionAgingRow>>(`${this.baseUrl}/reports/exception-aging`, { params: this.reportParams(companyId, filter) });
    }

    private reportParams(companyId: number, filter: BankReconciliationReportFilter): HttpParams {
        let params = new HttpParams()
            .set('companyId', String(companyId))
            .set('page', String(filter.page ?? 0))
            .set('size', String(filter.size ?? 50));
        if (filter.bankAccountId !== undefined) params = params.set('bankAccountId', String(filter.bankAccountId));
        if (filter.sessionId !== undefined) params = params.set('sessionId', String(filter.sessionId));
        if (filter.statementId !== undefined) params = params.set('statementId', String(filter.statementId));
        if (filter.dateFrom) params = params.set('dateFrom', filter.dateFrom);
        if (filter.dateTo) params = params.set('dateTo', filter.dateTo);
        if (filter.status) params = params.set('status', filter.status);
        if (filter.searchText) params = params.set('searchText', filter.searchText);
        return params;
    }
}
